import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, X, Moon, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "uneksia_morning_prompt_last_shown";

/**
 * Morning dream-recall prompt.
 * Shows once per day between 05:00–12:00 to remind the user to log
 * the dream they had last night while it's still fresh in memory.
 */
export default function MorningDreamPrompt({ user }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;

    const now = new Date();
    const hour = now.getHours();
    if (hour < 5 || hour >= 12) return;

    const today = now.toISOString().slice(0, 10);
    const lastShown = localStorage.getItem(STORAGE_KEY);
    if (lastShown === today) return;

    const timer = setTimeout(() => {
      setOpen(true);
      localStorage.setItem(STORAGE_KEY, today);
    }, 1500);

    return () => clearTimeout(timer);
  }, [user]);

  const handleWriteDream = () => {
    setOpen(false);
    navigate("/journal");
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-void/40 backdrop-blur-sm animate-in fade-in duration-300"
      data-testid="morning-prompt-overlay"
      onClick={handleClose}
    >
      <div
        className="relative max-w-md w-full glass-effect rounded-3xl p-8 shadow-2xl border border-white/60 animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
        data-testid="morning-prompt-modal"
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-void/40 hover:text-void/80 transition-colors"
          aria-label="Sulje"
          data-testid="morning-prompt-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-200 to-indigo-200 rounded-full blur-xl opacity-60 animate-pulse" />
            <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center">
              <Moon className="w-8 h-8 text-purple-600" />
            </div>
          </div>

          <h2 className="font-heading text-3xl font-light text-void mb-3 leading-tight">
            {t("morningPrompt.title")}
          </h2>

          <p className="font-body text-void/70 text-base mb-2">
            {t("morningPrompt.subtitle")}
          </p>

          <p className="font-body text-void/50 text-sm italic mb-8">
            {t("morningPrompt.tip")}
          </p>

          <div className="flex flex-col w-full gap-3">
            <Button
              onClick={handleWriteDream}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-full py-6 text-base font-body shadow-lg hover:shadow-xl transition-all"
              data-testid="morning-prompt-write-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              {t("morningPrompt.writeNow")}
            </Button>

            <button
              onClick={handleClose}
              className="text-void/50 hover:text-void/80 text-sm font-body transition-colors py-2"
              data-testid="morning-prompt-later"
            >
              {t("morningPrompt.later")}
            </button>
          </div>

          <div className="mt-6 flex items-center gap-1.5 text-xs text-void/40 font-body">
            <Sparkles className="w-3 h-3" />
            <span>{t("morningPrompt.streakHint")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
